import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CardContainer from 'src/components/common/CardContainer.redesign';
import Filter from 'src/components/common/Filter';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ROWLine from 'src/models/ROWLine';
import SearchListData from 'src/models/SearchListData';
import { IRootState } from 'src/reducers';
import { ODataOptions } from 'src/util/ODataBuilder';
import useFetch from 'use-http';
import ROWListCard from '../../TractComponents/ROWListCard';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import PagedResultSet from 'src/models/common/PagedResultSet';
import County from 'src/models/County';
import { ContextMenuAction } from 'src/components/common/ContextMenu';

interface IROWLineListProps {
    tagFilterUpdated?: boolean;
    resetTagFilterCallback?: () => void;
    refreshProjectTagsCallback?: () => void;
    reloadCallback?: () => void;
    getDataOptions?: () => ODataOptions;
    setDataOptions?: (options: ODataOptions) => void;
    setTractListId: (tractListId: number) => void;
    searchFilterData?: SearchListData[];
    filterLines?: boolean;
}

const ROWLineList: React.FC<IROWLineListProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    // const dialogContext = React.useContext<dialogCreators>(DialogContext);
    const { get, response } = useFetch();

    const [projectCounties, setProjectCounties] = useState<PagedResultSet<County>>(undefined);
    const [rowLineDataOptions, setRowLineDataOptions] = useState<ODataOptions>(undefined);
    const [rowLines, setRowLines] = useState<ROWLine[]>([]);
    const [selectedLine, setSelectedLine] = useState<number>(undefined);
    // const [resetLastPage, setResetLastPage] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getProjectCounties();
        loadRowLines('');
    }, []);

    useEffect(() => {
        if (selectedLine > 0) {
            props.setTractListId(selectedLine);
        }
    }, [selectedLine]);

    const rowLineSearchUpdate = useCallback(
        _.debounce((filter: string) => {
            if (filter) {
                loadRowLines(filter);
            }
        }, 800), []
    );

    const getProjectCounties = async () => {
        const data = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/counties`);

        if (response.ok) {
            setProjectCounties(data);
        }
        setLoading(false);
    };

    const loadRowLines = async (filter: string) => {
        const data = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/row-lines`);

        if (response.ok) {
            setRowLines(data);
        }
        setLoading(false);
        // setResetLastPage(false);
    };

    const updateRowLineFilter = (options: ODataOptions) => {
        let tempDataOptions = rowLineDataOptions === undefined ? new ODataOptions() : rowLineDataOptions;
        tempDataOptions.setIsNextpage(false);
        tempDataOptions.addPaging(1, rowLineDataOptions?.getPageSize());
        let hadOmniFilters = tempDataOptions?.toString().search('omni') > 0 ? true : false;
        let hadCustomFilters = tempDataOptions?.toString().search('where') > 0 ? true : false;
        let hasOrderby = options?.toString().search('orderby') > 0 ? true : false;

        if (tempDataOptions?.clearFilters && (options.hasFilters() || hadOmniFilters || hadCustomFilters || hasOrderby)) {
            if (hadOmniFilters) {
                tempDataOptions?.clearFilters('omni');
            }
            if (hadCustomFilters) {
                tempDataOptions?.clearFilters('where');
            }
            if (tempDataOptions) {
                tempDataOptions.merge(options);
                const stringOptions = tempDataOptions.toString();
                if (stringOptions) {
                    rowLineSearchUpdate(stringOptions);
                    setRowLineDataOptions(tempDataOptions);
                    console.log('New Filter Options', stringOptions);
                }
            }
        }
    };

    const openROWLineDialog = async (rowLine: ROWLine) => {
        dialogContext.project.addEditROWLine(
            rowLine,
            projectCounties?.items,
            () => loadRowLines('')
        );
    };

    const handleRowLineSelection = (lineNumber: number) => {
        setSelectedLine(lineNumber);
    };

    const rowLineList = () => {
        // TODO: Get A proper number for the ROW Line instead of using ID.
        if (!rowLines || rowLines.length === 0) {
            return (
                <div style={{ padding: '2rem', border: '2px red solid', margin: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1em' }}>
                    No ROW Lines Found For Project
                </div>
            );
        }
        if (loading === true) {
            return (
                <div style={{ padding: '2rem', border: '2px red solid', margin: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1em' }}>
                    Loading Data...
                </div>
            );
        }

        return (
            <CardContainer>
                {rowLines?.map((rowLine: ROWLine) => {
                    let rowLineActions: ContextMenuAction[] = [{ text: 'Edit', action: () => openROWLineDialog(rowLine)}];

                    return (
                        <ROWListCard
                            key={rowLine.id.toString()}
                            rowLine={rowLine}
                            actions={rowLineActions}
                            onClick={() => handleRowLineSelection(rowLine.lineNumber)}
                            isActive={selectedLine === rowLine.lineNumber}
                        />
                    );
                })}
            </CardContainer>
        );
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '1rem', background: '#ddd', padding: '.8rem' }}>
                <div style={{ height: '100%', verticalAlign: 'center', fontWeight: 'bold', fontSize: '1.3em' }}>
                    ROW Lines
                </div>
                <div>
                    <Button size="small" intent="primary" text="Create ROW Line" disabled={loading} onClick={() => openROWLineDialog(null)} />
                </div>
            </div>
            <Filter
                sortItems={[
                    { group: 1, text: 'Name', icon: 'alpha-up', orderName: 'TractName', orderDirection: 'ascending' },
                    { group: 1, text: 'Name Desc', icon: 'alpha-down', orderName: 'TractName', orderDirection: 'descending' },
                    { group: 1, text: 'Number', icon: 'numeric-up', orderName: 'Number', orderDirection: 'ascending' },
                    { group: 1, text: 'Number Desc', icon: 'numeric-down', orderName: 'Number', orderDirection: 'descending' },
            ]}
                onFilterChanged={updateRowLineFilter}
            />
            {rowLineList()}

            {/* <AddTract
                sectionCounties={counties}
                createTract={createTract}
                hideDialog={() => dispatch(actions.layout.hideDialog())}
                sectionNumber={props.landSystem === LandSystem.Eastern ? undefined : props.sectionNumber}
                mapNumber={props.landSystem === LandSystem.Eastern ? props.sectionNumber : undefined}
            /> */}
            {/* <Dialog dialogName={DialogList.CONFIRM_TRACT_DELETE} title="Confirm Delete Tract">
                <div>Are you sure that you want to permanently delete the selected tract?</div>
                <div className="mt-2 button-row">
                    <Button
                        text="OK"
                        onClick={props.tractType === 'ROW Line' ? onConfirmDeleteSurfaceTract : onConfirmDeleteTract}
                        intent="primary"
                    />
                    <Button text="Cancel" onClick={() => onHideDialog(false)} />
                </div>
            </Dialog> */}
        </>
    );

};

export default ROWLineList;


// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/ROWLineList.tsx